import { useMemo, useState } from 'react';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type LayerVisibility = {
  /** Whether the layer is visible or hidden */
  isVisible: boolean;
  /** Opacity level of the layer, from 0 (fully transparent) to 1 (fully opaque) */
  opacity: number;
  /** Function to update the visibility state */
  setVisible: (value: boolean) => void;
  /** Function to update the opacity level */
  setOpacity: (value: number) => void;
};

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

/**
 * Manage the visibility and opacity state of a canvas layer.
 *
 * @example
 * ```tsx
 * const blastLayerVisibility = useLayerVisibility({ visible: true, opacity: 0.8 });
 * ```
 */
const useLayerVisibility = (initial?: { visible?: boolean; opacity?: number }): LayerVisibility => {
  const [isVisible, setVisible] = useState(initial?.visible ?? true);
  const [opacity, setOpacity] = useState(initial?.opacity ?? 1);

  return useMemo(
    () => ({
      isVisible,
      opacity,
      setVisible,
      setOpacity,
    }),
    [isVisible, opacity],
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { LayerVisibility };
export { useLayerVisibility };
